import { type ReactNode } from "react";
import { IoArrowBackOutline } from "react-icons/io5";
import { Link, useNavigate } from "react-router";
import styled from "styled-components";

import {
    confirmUnsavedChanges,
    type UnsavedChangesContext,
} from "../modules/unsaved-changes";

const Header = styled.div`
    position: fixed;
    top: 16px;
    left: 32px;
    display: flex;
    align-items: center;
    gap: 16px;
    z-index: 10;
`;

const Title = styled.span<{ $light?: boolean }>`
    color: transparent;
    text-decoration: none;
    font-size: 2.4rem;
    -webkit-text-stroke: 1px ${ (p) => p.$light ? "#fff" : "#000" };
    word-spacing: 100vw;
    font-weight: bold;
    line-height: 1em;
    letter-spacing: -.05em;

    &:hover {
        color: ${ (p) => p.$light ? "white" : "black" };
    }
`;

const MainPageLink = styled(Link)<{ $light?: boolean }>`
    align-items: center;
    align-self: center;
    background: ${ (p) => p.$light ? "rgba(255, 255, 255, 0.12)" : "#fff" };
    border: 1px solid ${ (p) => p.$light ? "rgba(255, 255, 255, 0.5)" : "#c1c7d0" };
    border-radius: 999px;
    color: ${ (p) => p.$light ? "#fff" : "#172b4d" };
    display: inline-flex;
    font-size: 0.9rem;
    font-weight: 600;
    gap: 6px;
    padding: 7px 16px;
    text-decoration: none;
    white-space: nowrap;

    &:hover {
        background: ${ (p) => p.$light ? "rgba(255, 255, 255, 0.24)" : "#f4f5f7" };
        border-color: ${ (p) => p.$light ? "#fff" : "#0052cc" };
        color: ${ (p) => p.$light ? "#fff" : "#0052cc" };
    }
`;

interface AppHeaderProps {
    title?: ReactNode;
    light?: boolean;
    guard?: UnsavedChangesContext;
}

export default function AppHeader({ title, light, guard }: AppHeaderProps) {
    const navigate = useNavigate();

    async function handleNavigate(event: React.MouseEvent, to: string) {
        if (!guard) return; // no guard supplied — default Link behaviour
        event.preventDefault();
        if (await confirmUnsavedChanges(guard)) navigate(to);
    }

    return (
        <Header>
            { title && (
                <Title
                    $light={ light }
                    as={ Link }
                    to=""
                    onClick={ (e) => handleNavigate(e, "") }
                >{ title }</Title>
            ) }
            <MainPageLink
                $light={ light }
                to="/"
                onClick={ (e) => handleNavigate(e, "/") }
            >
                <IoArrowBackOutline /> Main page
            </MainPageLink>
        </Header>
    );
}
